import React, { useState } from 'react';
import { useT } from '@/i18n/labels';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { id: string };

/**
 * A password field with a show/hide (eye) toggle, so a user setting a new password can
 * see what they typed. Same component and behaviour as admin-portal's PasswordInput: both
 * sites run one change-password process. The toggle is a real <button type="button"> (never
 * submits the form), keyboard reachable, and announces its state via aria-label/aria-pressed.
 * The icon sits at the inline END, so it flips sides automatically in RTL.
 */
export const PasswordInput: React.FC<Props> = ({ id, style, ...rest }) => {
  const { t } = useT();
  const [shown, setShown] = useState(false);
  const label = shown ? t('passwordHide') : t('passwordShow');

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        className="ac-field"
        style={{ paddingInlineEnd: 42, ...style }}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={label}
        aria-pressed={shown}
        title={label}
        data-testid={`${id}-toggle`}
        style={{
          position: 'absolute',
          insetInlineEnd: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          padding: 4,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          opacity: 0.65,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {shown ? (
            <>
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </>
          ) : (
            <>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
};

export default PasswordInput;
